var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
import React from 'react';
import { jsx, css } from '@emotion/react';
import classNames from 'classnames';
import * as ReactRedux from 'react-redux';
import Immutable from 'seamless-immutable';
import { debounce } from 'lodash-es';
import arrowLeftOutlined from 'jimu-icons/svg/outlined/directional/arrow-left.svg';
import { useTranslation } from '../../hooks';
import DataSourceManager from '../../data-source-manager';
import defaultMessages from '../../translations/default';
import FeatureInfo from './feature-info';
import pointer from 'jimu-icons/svg/outlined/editor/pointer.svg';
export var SelectionModeType;
(function (SelectionModeType) {
    SelectionModeType["Single"] = "SINGLE";
    SelectionModeType["Multiple"] = "MULTIPLE";
})(SelectionModeType || (SelectionModeType = {}));
const styles = css `
  .related-records-header {
    height: 32px;
    .related-records-title {
      overflow:hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .related-record-select {
      .jimu-dropdown-button {
        .related-record-select-icon {
          display: block !important;
          margin-right: 0;
        }
        .related-record-select-text {
          display: none;
        }
      }
      .jimu-dropdown-menu {
        max-width: 100vw !important;
      }
    }
  }
  .related-records-container {
    gap: var(--sys-spacing-2);
    .related-record {
      border: 1px solid var(--sys-color-divider-primary);
    }
    .related-record-selected {
      border-color: var(--sys-color-primary-main);
    }
  }
`;
const RECORD_LOAD_SIZE = 20;
export default function RelatedRecords(props) {
    var _a, _b;
    const { widgetId, relatedDs, relatedIds, version, dataViewInfo, showBack, onBack, uiComponents } = props;
    const [loading, setLoading] = React.useState(false);
    const [selectionMode, setSelectionMode] = React.useState(SelectionModeType.Single);
    const handleChangeSelectionMode = React.useCallback((evt) => {
        setSelectionMode(evt.target.value);
    }, []);
    // create related data view
    const [relatedDataView, setRelatedDataView] = React.useState(null);
    React.useEffect(() => {
        if (!relatedIds || relatedIds.length === 0)
            return;
        const localId = `related_popper_${version}`;
        const dataView = relatedDs.getLocalDataSource(localId);
        if (dataView) {
            updateDataView(dataView);
        }
        else {
            DataSourceManager.getInstance().createDataSource(Immutable({ id: relatedDs.id }), null, localId).then((newDataView) => {
                updateDataView(newDataView);
            });
        }
        function updateDataView(dataView) {
            const idField = relatedDs.getIdField();
            dataView.updateQueryParams({
                where: `${idField} in (${relatedIds.join(',')})`
            }, 'related-records');
            setRelatedDataView(dataView);
            // store data view, destroy them when closing popper
            if (!dataViewInfo[version]) {
                dataViewInfo[version] = [];
            }
            dataViewInfo[version].push(dataView);
        }
    }, [relatedDs, widgetId, relatedIds, dataViewInfo, version]);
    // handle set-filter action
    const [filterIds, setFilterIds] = React.useState(null);
    const dataActionFilter = ReactRedux.useSelector((state) => { var _a, _b, _c; return (_c = (_b = (_a = state.dataSourcesInfo) === null || _a === void 0 ? void 0 : _a[relatedDataView === null || relatedDataView === void 0 ? void 0 : relatedDataView.id]) === null || _b === void 0 ? void 0 : _b.widgetQueries) === null || _c === void 0 ? void 0 : _c[`setFilter-${relatedDataView === null || relatedDataView === void 0 ? void 0 : relatedDataView.id}`]; });
    React.useEffect(() => {
        var _a;
        if (!dataActionFilter || dataActionFilter.where === '1=1') {
            setFilterIds(null);
        }
        else {
            const realQuery = relatedDataView === null || relatedDataView === void 0 ? void 0 : relatedDataView.getRealQueryParams({ returnIdsOnly: true }, 'query');
            setLoading(true);
            (_a = relatedDataView === null || relatedDataView === void 0 ? void 0 : relatedDataView.query(realQuery)) === null || _a === void 0 ? void 0 : _a.then((result) => {
                var _a, _b;
                setFilterIds((_b = (_a = result === null || result === void 0 ? void 0 : result.records) === null || _a === void 0 ? void 0 : _a.map) === null || _b === void 0 ? void 0 : _b.call(_a, r => r.getId()));
            }).catch(e => {
                console.error(e);
                setFilterIds(null);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [dataActionFilter, relatedDataView]);
    // construct actionDataSets
    const [allLoadedRecords, setAllLoadedRecords] = React.useState([]);
    const noSelectedIds = React.useMemo(() => [], []);
    const selectedIds = ((_b = (_a = ReactRedux.useSelector((state) => { var _a, _b, _c; return ((_c = (_b = (_a = state.dataSourcesInfo) === null || _a === void 0 ? void 0 : _a[relatedDataView === null || relatedDataView === void 0 ? void 0 : relatedDataView.id]) === null || _b === void 0 ? void 0 : _b.selectedIds) === null || _c === void 0 ? void 0 : _c.join(',')) || ''; })) === null || _a === void 0 ? void 0 : _a.split) === null || _b === void 0 ? void 0 : _b.call(_a, ',')) || noSelectedIds;
    const dsLabel = relatedDs.getLabel();
    const validFields = React.useMemo(() => {
        const allUsedFields = relatedDs.getAllUsedFields();
        const allFields = relatedDs.getLayerDefinition().fields.map(f => f.name);
        const validFields = allUsedFields === '*' ? allFields : allUsedFields;
        return validFields;
    }, [relatedDs]);
    const actionDataSets = React.useMemo(() => {
        if (!relatedDataView)
            return [];
        const loadedRecords = Array.isArray(filterIds) ? allLoadedRecords.filter(r => filterIds.includes(r.getId())) : allLoadedRecords;
        const selectedRecords = loadedRecords.filter(r => selectedIds.includes(r.getId()));
        return [{
                dataSource: relatedDataView,
                type: selectedRecords.length > 0 ? 'selected' : 'loaded',
                records: selectedRecords.length > 0 ? selectedRecords : loadedRecords,
                name: dsLabel,
                fields: validFields
            }];
    }, [relatedDataView, filterIds, allLoadedRecords, dsLabel, validFields, selectedIds]);
    // load related records by relatedIds
    const loadRelatedRecords = React.useCallback((loadedRecordSize_1, ...args_1) => __awaiter(this, [loadedRecordSize_1, ...args_1], void 0, function* (loadedRecordSize, initial = false) {
        if (!relatedDataView || !relatedIds)
            return;
        setLoading(true);
        try {
            const dataViewRecords = relatedDataView.getRecords();
            const allLoadedRecordIds = dataViewRecords.map(r => r.getId());
            const allLoadedRecordIdsSet = new Set(allLoadedRecordIds);
            const loadRecordIds = relatedIds.filter(id => !allLoadedRecordIdsSet.has(id) && (!filterIds || filterIds.includes(id))).slice(0, loadedRecordSize);
            let loadRecords;
            if (loadRecordIds.length > 0) {
                const queryParams = {
                    where: `${relatedDs.getIdField()} in (${loadRecordIds.join(',')})`,
                    outFields: validFields,
                    returnGeometry: true
                };
                const queryResult = yield relatedDataView.query(queryParams);
                loadRecords = ((queryResult === null || queryResult === void 0 ? void 0 : queryResult.records) || []);
            }
            else {
                loadRecords = [];
            }
            let newRecords;
            if (initial) {
                newRecords = dataViewRecords.filter(r => relatedIds.includes(r.getId())).concat(loadRecords);
            }
            else {
                newRecords = dataViewRecords.concat(loadRecords);
            }
            relatedDataView.setRecords(newRecords);
            setAllLoadedRecords(relatedDataView.getRecords());
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setLoading(false);
        }
    }), [filterIds, relatedDataView, relatedDs, relatedIds, validFields]);
    // infinity scrolling
    const handleScroll = debounce((e) => {
        if (allLoadedRecords.length >= relatedIds.length || (filterIds && allLoadedRecords.length > filterIds.length))
            return;
        const div = e.target;
        if (div.scrollHeight === div.clientHeight + div.scrollTop) {
            loadRelatedRecords(RECORD_LOAD_SIZE);
        }
    }, 100);
    // initial load
    React.useEffect(() => {
        loadRelatedRecords(RECORD_LOAD_SIZE, true);
    }, [loadRelatedRecords]);
    const filteredLoadedRecords = Array.isArray(filterIds) ? allLoadedRecords.filter(r => filterIds.includes(r.getId())) : allLoadedRecords;
    // select records
    const handleSelect = React.useCallback((record) => {
        const id = record.getId();
        let newSelectedIds = selectedIds;
        if (selectionMode === SelectionModeType.Single) {
            if (selectedIds.length === 1 && selectedIds.includes(id)) {
                newSelectedIds = [];
            }
            else {
                newSelectedIds = [id];
            }
        }
        else if (selectionMode === SelectionModeType.Multiple) {
            if (selectedIds.includes(id)) {
                newSelectedIds = newSelectedIds.filter(selectedId => selectedId !== id);
            }
            else {
                newSelectedIds.push(id);
            }
        }
        const selectedRecords = allLoadedRecords.filter(r => newSelectedIds.includes(r.getId()));
        relatedDataView.selectRecords({ widgetId, records: selectedRecords });
    }, [allLoadedRecords, relatedDataView, selectedIds, selectionMode, widgetId]);
    // popupTemplate for FeatureInfo
    const popupTemplate = React.useMemo(() => {
        var _a;
        const fields = Object.values(relatedDs.getSchema().fields);
        const fieldNames = fields.map(f => f.name);
        const popupInfo = relatedDs.getPopupInfo();
        let fieldInfos = ((popupInfo === null || popupInfo === void 0 ? void 0 : popupInfo.fieldInfos) || []).filter(f => f.visible && fieldNames.includes(f.fieldName))
            .map(f => ({ fieldName: f.fieldName, label: f.label }));
        if (fieldInfos.length === 0) {
            fieldInfos = fields.filter(f => validFields.includes(f.name)).map(f => ({ fieldName: f.name, label: f.alias }));
        }
        const displayField = (_a = relatedDs.getLayerDefinition()) === null || _a === void 0 ? void 0 : _a.displayField;
        const idField = relatedDs.getIdField();
        const titleField = validFields.find(f => f === displayField || /(name|title|label|id)/i.test(f) || f === idField);
        return {
            title: `${titleField}: {${titleField}}`,
            content: [{ type: 'fields' }],
            fieldInfos
        };
    }, [relatedDs, validFields]);
    const { Loading, LoadingType, Button, Select, Icon, DataActionList, DataActionListStyle } = uiComponents;
    const translate = useTranslation(defaultMessages);
    return (jsx("div", { className: 'related-records h-100 d-flex flex-column', css: styles },
        loading && jsx(Loading, { type: LoadingType.Secondary }),
        jsx("div", { className: 'related-records-header flex-shrink-0 d-flex justify-content-between align-items-center' },
            jsx("div", { className: 'related-records-title flex-grow-1' },
                showBack && jsx(Button, { className: 'p-0 mr-2', size: 'sm', type: 'tertiary', icon: true, onClick: onBack },
                    jsx(Icon, { icon: arrowLeftOutlined, autoFlip: true })),
                dsLabel),
            jsx("div", { className: 'd-flex align-items-center' },
                jsx(Select, { className: 'related-record-select', value: selectionMode, size: 'sm', appendToBody: false, onChange: handleChangeSelectionMode, title: translate('selectionMode') },
                    jsx("option", { key: SelectionModeType.Single, value: SelectionModeType.Single },
                        jsx(Icon, { className: 'related-record-select-icon d-none', size: 12, icon: pointer }),
                        jsx("span", { className: 'related-record-select-text' }, translate('selectionModeSingle'))),
                    jsx("option", { key: SelectionModeType.Multiple, value: SelectionModeType.Multiple },
                        jsx(Icon, { className: 'related-record-select-icon d-none', size: 12, icon: pointer }),
                        jsx("span", { className: 'related-record-select-text' }, translate('selectionModeMultiple')))),
                jsx(DataActionList, { widgetId: widgetId, dataSets: actionDataSets, listStyle: DataActionListStyle.Dropdown, buttonSize: 'sm', buttonType: 'tertiary' }))),
        jsx("div", { className: 'related-records-container d-flex flex-column flex-grow-1 overflow-auto p-1', role: 'listbox', onScroll: handleScroll }, filteredLoadedRecords.map(r => {
            const id = r.getId();
            const isSelected = (selectedIds || []).includes(id);
            return jsx("div", { key: id, className: classNames('related-record', { 'related-record-selected': isSelected }), onClick: () => { handleSelect(r); } },
                jsx(FeatureInfo, { graphic: r.feature, popupTemplate: popupTemplate, uiComponents: uiComponents }));
        }))));
}
